export type MarketplaceHirePlanId = "intern" | "part_time" | "full_time";

export type MarketplaceHireAddonId =
  | "emr_integration"
  | "agent_phone_number"
  | "agent_email";

export type MarketplaceCommunicationOptionId = "agent_phone_number" | "agent_email";

export type MarketplaceSchedulingIntegrationId =
  | "none"
  | "google_calendar"
  | "calendly"
  | "zoom"
  | "zapier"
  | "juvonno"
  | "dentrix"
  | "dentrix_ascend"
  | "open_dental"
  | "eaglesoft"
  | "curve"
  | "denticon";

export type MarketplaceHireTool = {
  label: string;
  included: boolean;
};

export type MarketplaceHirePlan = {
  id: MarketplaceHirePlanId;
  label: string;
  subtitle: string;
  priceCents: number;
  /** Use `{agentName}` as a placeholder for the agent display name. */
  descriptionTemplate: string;
  skills: string[];
  tools: MarketplaceHireTool[];
  defaultTeamMembers: number;
  defaultLocations: number;
  includedTeamMembers: number;
  includedLocations: number;
  unlimitedTeamMembers?: boolean;
  unlimitedLocations?: boolean;
  /** Communication options included at no extra cost on this plan. */
  includedCommunication: MarketplaceCommunicationOptionId[];
  showTeamMembersCalculator: boolean;
  showLocationsCalculator: boolean;
  showCommunicationSection: boolean;
};

export type MarketplaceSchedulingIntegration = {
  id: MarketplaceSchedulingIntegrationId;
  label: string;
  priceCents: number;
};

export type MarketplaceHireAddon = {
  id: MarketplaceHireAddonId;
  label: string;
  priceCents: number;
  description: string;
  availableForPlans: MarketplaceHirePlanId[];
};

export const TEAM_MEMBER_MONTHLY_CENTS = 1000;
export const LOCATION_MONTHLY_CENTS = 1500;
export const COMMUNICATION_MONTHLY_CENTS = 1000;

export type MarketplaceCommunicationOption = {
  id: MarketplaceCommunicationOptionId;
  label: string;
  priceCents: number;
  description: string;
};

export const MARKETPLACE_COMMUNICATION_OPTIONS: MarketplaceCommunicationOption[] = [
  {
    id: "agent_phone_number",
    label: "Agent Phone Number",
    priceCents: COMMUNICATION_MONTHLY_CENTS,
    description: "Dedicated phone number for inbound and outbound patient calls.",
  },
  {
    id: "agent_email",
    label: "Agent Email",
    priceCents: COMMUNICATION_MONTHLY_CENTS,
    description: "Dedicated email address for patient communication and notifications.",
  },
];

export const MARKETPLACE_SCHEDULING_INTEGRATIONS: MarketplaceSchedulingIntegration[] = [
  { id: "none", label: "None", priceCents: 0 },
  { id: "google_calendar", label: "Google Calendar", priceCents: 500 },
  { id: "calendly", label: "Calendly", priceCents: 500 },
  { id: "zoom", label: "Zoom", priceCents: 0 },
  { id: "zapier", label: "Zapier", priceCents: 0 },
  { id: "juvonno", label: "Juvonno", priceCents: 5000 },
  { id: "dentrix", label: "Dentrix", priceCents: 5000 },
  { id: "dentrix_ascend", label: "Dentrix Ascend", priceCents: 5000 },
  { id: "open_dental", label: "Open Dental", priceCents: 5000 },
  { id: "eaglesoft", label: "Eaglesoft", priceCents: 5000 },
  { id: "curve", label: "Curve", priceCents: 5000 },
  { id: "denticon", label: "Denticon", priceCents: 5000 },
];

export const MARKETPLACE_HIRE_PLANS: MarketplaceHirePlan[] = [
  {
    id: "intern",
    label: "Intern",
    subtitle: "For sole proprietors",
    priceCents: 0,
    descriptionTemplate:
      "Hire {agentName} as a free intern to engage visitors on your website, answer questions, gather contact information, capture leads, and deliver detailed conversation reports.",
    skills: ["Live Chat"],
    tools: [{ label: "Zapier", included: true }],
    defaultTeamMembers: 1,
    defaultLocations: 1,
    includedTeamMembers: 0,
    includedLocations: 0,
    includedCommunication: [],
    showTeamMembersCalculator: false,
    showLocationsCalculator: false,
    showCommunicationSection: false,
  },
  {
    id: "part_time",
    label: "Part-time",
    subtitle: "For growing companies",
    priceCents: 9900,
    descriptionTemplate:
      "Hire {agentName} part-time to engage visitors via live chat and video calls on your website, answer questions, gather contact information, capture leads, schedule appointments, and deliver detailed conversation reports.",
    skills: ["Live Chat", "Video Calls"],
    tools: [{ label: "Zapier", included: true }],
    defaultTeamMembers: 1,
    defaultLocations: 1,
    includedTeamMembers: 1,
    includedLocations: 1,
    includedCommunication: ["agent_phone_number", "agent_email"],
    showTeamMembersCalculator: true,
    showLocationsCalculator: true,
    showCommunicationSection: true,
  },
  {
    id: "full_time",
    label: "Full-time",
    subtitle: "For enterprises",
    priceCents: 29900,
    descriptionTemplate:
      "Hire {agentName} full-time to answer phone calls and engage visitors via live chat and video calls on your website, answer questions, gather contact information, capture leads, schedule appointments, and deliver detailed conversation reports.",
    skills: ["Live Chat", "Video Calls", "Phone Calls"],
    tools: [{ label: "Zapier", included: true }],
    defaultTeamMembers: 10,
    defaultLocations: 10,
    includedTeamMembers: 0,
    includedLocations: 0,
    unlimitedTeamMembers: true,
    unlimitedLocations: true,
    includedCommunication: ["agent_phone_number", "agent_email"],
    showTeamMembersCalculator: true,
    showLocationsCalculator: true,
    showCommunicationSection: true,
  },
];

export const MARKETPLACE_HIRE_ADDONS: MarketplaceHireAddon[] = [];

export function formatHirePrice(cents: number): string {
  if (cents === 0) return "$0";
  if (cents % 100 === 0) return `$${cents / 100}`;
  return `$${(cents / 100).toFixed(2)}`;
}

/** Price shown on plan tier tabs. */
export function formatPlanTabPrice(cents: number): { main: string; sub?: string } {
  if (cents === 0) return { main: "FREE", sub: "$0/mo" };
  return { main: formatHirePrice(cents), sub: "/mo" };
}

/** Total at bottom of pricing panel. */
export function formatHireTotalPrice(cents: number): string {
  return `${formatHirePrice(cents)}/mo`;
}

export function formatPlanDescription(plan: MarketplaceHirePlan, agentName: string): string {
  return plan.descriptionTemplate.replace(/\{agentName\}/g, agentName);
}

export function getHirePlan(id: MarketplaceHirePlanId): MarketplaceHirePlan {
  return MARKETPLACE_HIRE_PLANS.find((p) => p.id === id) ?? MARKETPLACE_HIRE_PLANS[0]!;
}

export function getSchedulingIntegration(
  id: MarketplaceSchedulingIntegrationId,
): MarketplaceSchedulingIntegration {
  return (
    MARKETPLACE_SCHEDULING_INTEGRATIONS.find((i) => i.id === id) ??
    MARKETPLACE_SCHEDULING_INTEGRATIONS[0]!
  );
}

export function computeTeamMemberCostCents(
  plan: MarketplaceHirePlan,
  teamMemberCount: number,
): number {
  if (plan.unlimitedTeamMembers) return 0;
  const extra = Math.max(0, teamMemberCount - plan.includedTeamMembers);
  return extra * TEAM_MEMBER_MONTHLY_CENTS;
}

export function computeLocationCostCents(
  plan: MarketplaceHirePlan,
  locationCount: number,
): number {
  if (plan.unlimitedLocations) return 0;
  const extra = Math.max(0, locationCount - plan.includedLocations);
  return extra * LOCATION_MONTHLY_CENTS;
}

export function computeCommunicationCostCents(
  plan: MarketplaceHirePlan,
  communicationOptionIds: MarketplaceCommunicationOptionId[],
): number {
  const included = new Set(plan.includedCommunication);
  return communicationOptionIds.reduce((sum, id) => {
    if (included.has(id)) return sum;
    const option = MARKETPLACE_COMMUNICATION_OPTIONS.find((o) => o.id === id);
    return sum + (option?.priceCents ?? COMMUNICATION_MONTHLY_CENTS);
  }, 0);
}

export function defaultCommunicationOptionIds(
  plan: MarketplaceHirePlan,
): MarketplaceCommunicationOptionId[] {
  return [...plan.includedCommunication];
}

export function getAddonsForPlan(planId: MarketplaceHirePlanId): MarketplaceHireAddon[] {
  return MARKETPLACE_HIRE_ADDONS.filter((addon) => addon.availableForPlans.includes(planId));
}

export function filterAddonsForPlan(
  planId: MarketplaceHirePlanId,
  addonIds: MarketplaceHireAddonId[],
): MarketplaceHireAddonId[] {
  const allowed = new Set(getAddonsForPlan(planId).map((a) => a.id));
  return addonIds.filter((id) => allowed.has(id));
}

export function computeHireTotalCents(selection: HirePlanSelection): number {
  const plan = getHirePlan(selection.planId);
  const validAddonIds = filterAddonsForPlan(selection.planId, selection.addonIds ?? []);
  const addonTotal = validAddonIds.reduce((sum, id) => {
    const addon = MARKETPLACE_HIRE_ADDONS.find((a) => a.id === id);
    return sum + (addon?.priceCents ?? 0);
  }, 0);
  const teamCost = plan.showTeamMembersCalculator
    ? computeTeamMemberCostCents(plan, selection.teamMemberCount)
    : 0;
  const locationCost = plan.showLocationsCalculator
    ? computeLocationCostCents(plan, selection.locationCount)
    : 0;
  const schedulingCost = getSchedulingIntegration(selection.schedulingIntegrationId).priceCents;
  const communicationCost = plan.showCommunicationSection
    ? computeCommunicationCostCents(plan, selection.communicationOptionIds)
    : 0;
  return (
    plan.priceCents +
    addonTotal +
    teamCost +
    locationCost +
    schedulingCost +
    communicationCost
  );
}

/** @deprecated Use computeHireTotalCents with full selection object. */
export function computeHireTotalCentsLegacy(
  planId: MarketplaceHirePlanId,
  addonIds: MarketplaceHireAddonId[],
): number {
  const plan = getHirePlan(planId);
  return computeHireTotalCents({
    planId,
    addonIds,
    teamMemberCount: plan.defaultTeamMembers,
    locationCount: plan.defaultLocations,
    schedulingIntegrationId: "none",
    communicationOptionIds: defaultCommunicationOptionIds(plan),
  });
}

export const HIRE_PLAN_SESSION_KEY = "egpt-hire-plan-selection";

export type HirePlanSelection = {
  planId: MarketplaceHirePlanId;
  addonIds?: MarketplaceHireAddonId[];
  teamMemberCount: number;
  locationCount: number;
  schedulingIntegrationId: MarketplaceSchedulingIntegrationId;
  communicationOptionIds: MarketplaceCommunicationOptionId[];
};

export function defaultHirePlanSelection(planId: MarketplaceHirePlanId): HirePlanSelection {
  const plan = getHirePlan(planId);
  return {
    planId,
    addonIds: [],
    teamMemberCount: plan.defaultTeamMembers,
    locationCount: plan.defaultLocations,
    schedulingIntegrationId: "none",
    communicationOptionIds: defaultCommunicationOptionIds(plan),
  };
}

function normalizeCommunicationOptionIds(
  plan: MarketplaceHirePlan,
  ids: MarketplaceCommunicationOptionId[] | undefined,
): MarketplaceCommunicationOptionId[] {
  const valid = new Set(MARKETPLACE_COMMUNICATION_OPTIONS.map((o) => o.id));
  const selected = new Set(
    (ids ?? []).filter((id): id is MarketplaceCommunicationOptionId => valid.has(id)),
  );
  for (const included of plan.includedCommunication) {
    selected.add(included);
  }
  return MARKETPLACE_COMMUNICATION_OPTIONS.map((o) => o.id).filter((id) => selected.has(id));
}

export function normalizeHirePlanSelection(
  partial: Partial<HirePlanSelection> & { planId: MarketplaceHirePlanId },
): HirePlanSelection {
  const plan = getHirePlan(partial.planId);
  const defaults = defaultHirePlanSelection(partial.planId);
  return {
    planId: partial.planId,
    addonIds: filterAddonsForPlan(partial.planId, partial.addonIds ?? []),
    teamMemberCount: Math.max(1, partial.teamMemberCount ?? defaults.teamMemberCount),
    locationCount: Math.max(1, partial.locationCount ?? defaults.locationCount),
    schedulingIntegrationId: partial.schedulingIntegrationId ?? defaults.schedulingIntegrationId,
    communicationOptionIds: normalizeCommunicationOptionIds(
      plan,
      partial.communicationOptionIds,
    ),
  };
}

export function writeHirePlanSelection(selection: HirePlanSelection): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      HIRE_PLAN_SESSION_KEY,
      JSON.stringify(normalizeHirePlanSelection(selection)),
    );
  } catch {
    /* ignore */
  }
}

export function readHirePlanSelection(): HirePlanSelection | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(HIRE_PLAN_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HirePlanSelection> & { planId?: MarketplaceHirePlanId };
    if (!parsed.planId) return null;
    return normalizeHirePlanSelection({
      planId: parsed.planId,
      addonIds: parsed.addonIds,
      teamMemberCount: parsed.teamMemberCount,
      locationCount: parsed.locationCount,
      schedulingIntegrationId: parsed.schedulingIntegrationId,
      communicationOptionIds: parsed.communicationOptionIds,
    });
  } catch {
    return null;
  }
}
